import { Alert, Button, Checkbox, Group, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';

type Nalog = Shema['DelovniNalogPodrobnosti'];

/** {@code zakljucek}: »Zaključi« s kljukico za e-pošto; {@code obvestilo}: samo pošiljanje (»Obvesti stranko«). */
export type NacinObvestila = 'zakljucek' | 'obvestilo';

/**
 * Okno ob zaključku naloga in za »Obvesti stranko«: predogled e-pošte »vozilo je pripravljeno« (besedilo v
 * nastavitvah, skupina Delavnica). Pri zaključku je kljukica »Pošlji stranki e-pošto« privzeto vključena, če ima
 * stranka e-pošto; če se ne pošlje (ali ne uspe), ostane na nalogu gumb »Obvesti stranko«.
 */
export function ObvestiloStranke({
  nalog,
  nacin,
  zapri,
  posodobljen,
}: {
  nalog: Nalog;
  nacin: NacinObvestila | null;
  zapri: () => void;
  posodobljen: (n: Nalog) => void;
}) {
  return (
    <Modal
      opened={!!nacin}
      onClose={zapri}
      title={nacin === 'zakljucek' ? 'Zaključek naloga' : 'Obvesti stranko'}
      size="lg"
    >
      {nacin && <Vsebina nalog={nalog} nacin={nacin} zapri={zapri} posodobljen={posodobljen} />}
    </Modal>
  );
}

function Vsebina({
  nalog,
  nacin,
  zapri,
  posodobljen,
}: {
  nalog: Nalog;
  nacin: NacinObvestila;
  zapri: () => void;
  posodobljen: (n: Nalog) => void;
}) {
  const predogled = useQuery({
    queryKey: ['obvestilo-naloga', nalog.id],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/delavnica/nalogi/{id}/obvestilo', { signal, params: { path: { id: nalog.id! } } })),
    gcTime: 0,
  });
  const email = predogled.data?.email;
  const [poslji, setPoslji] = useState<boolean | null>(null);
  const posljem = nacin === 'obvestilo' || (poslji ?? !!email);

  const potrdi = useMutation({
    // zaslon naloga se ob novih podatkih postavi znova, zato se posodobi šele na koncu (okno ostane odprto do konca)
    mutationFn: async () => {
      let n = nalog;
      if (nacin === 'zakljucek') {
        n = await podatki(
          api.POST('/api/delavnica/nalogi/{id}/status', {
            params: { path: { id: nalog.id! } },
            body: { status: 'ZAKLJUCEN' },
          }),
        );
      }
      try {
        if (posljem && email) {
          n = await podatki(api.POST('/api/delavnica/nalogi/{id}/obvesti', { params: { path: { id: nalog.id! } } }));
          notifications.show({ color: 'green', message: `Stranka je obveščena (${email}).` });
        }
      } catch (e) {
        if (nacin !== 'zakljucek') {
          throw e;
        }
        // nalog je zaključen; gumb »Obvesti stranko« ostane za ponovni poskus
        notifications.show({ color: 'red', message: `Nalog je zaključen, e-pošta ni bila poslana: ${sporocilo(e)}` });
      }
      zapri();
      posodobljen(n);
    },
  });

  return (
    <Stack gap="sm">
      {predogled.isError && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(predogled.error)}
        </Alert>
      )}
      {nacin === 'zakljucek' && (
        <Text size="sm">
          Vozilo je pripravljeno za prevzem. Nalog se zaključi, stranka lahko dobi obvestilo po e-pošti.
        </Text>
      )}
      {predogled.data && (
        <>
          {email ? (
            <>
              {nacin === 'zakljucek' && (
                <Checkbox
                  label={`Pošlji stranki e-pošto (${email})`}
                  checked={posljem}
                  onChange={(e) => setPoslji(e.currentTarget.checked)}
                />
              )}
              {posljem && (
                <Paper withBorder radius="md" p="sm">
                  <Text size="sm" c="dimmed">
                    Za: {email}
                  </Text>
                  <Text size="sm" fw={700} mt={4}>
                    {predogled.data.zadeva}
                  </Text>
                  <Text size="sm" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                    {predogled.data.besedilo}
                  </Text>
                </Paper>
              )}
            </>
          ) : (
            <Alert color="yellow" variant="light">
              Stranka nima e-pošte, zato je ni mogoče obvestiti. E-pošto vpišete na zaslonu osebe.
            </Alert>
          )}
          <Text size="xs" c="dimmed">
            Besedilo sporočila spremenite v Nastavitvah, v skupini Delavnica.
          </Text>
        </>
      )}
      {potrdi.error && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(potrdi.error)}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={potrdi.isPending}>
          Prekliči
        </Button>
        <Button
          onClick={() => potrdi.mutate()}
          loading={potrdi.isPending}
          disabled={predogled.isPending || (nacin === 'obvestilo' && !email)}
        >
          {nacin === 'zakljucek' ? 'Zaključi' : 'Pošlji'}
        </Button>
      </Group>
    </Stack>
  );
}
